// Shared, forgiving search for Pharos Vault.
// - case, accent and punctuation insensitive ("bpc157" finds "BPC-157", "coq 10" finds "CoQ10")
// - every word must match somewhere (name, code, category, description)
// - results ranked: exact name > name starts with > name contains > code/category > description
// - "did you mean" suggestions when nothing matches

const GREEK = { "α": "alpha", "β": "beta", "γ": "gamma", "δ": "delta", "ω": "omega", "κ": "kappa", "μ": "mu" };

export function norm(s) {
  return String(s == null ? "" : s)
    .toLowerCase()
    .replace(/[αβγδωκμ]/g, ch => GREEK[ch] || ch)
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9+]+/g, " ")
    .trim();
}

const compact = s => s.replace(/\s+/g, "");

// Common shorthand people type.
const ALIASES = {
  vit: "vitamin", vits: "vitamin", coq: "coq10", nad: "nad+", nmn: "nicotinamide mononucleotide",
  tb500: "tb 500", glp: "glp 1", sema: "semaglutide", tirz: "tirzepatide", reta: "retatrutide",
  mag: "magnesium", zn: "zinc", omega3: "omega 3", fish: "omega", ala: "alpha lipoic",
};

// Pre-compute a search index for a list of records. Returns rows with a stable _id.
export function buildIndex(records) {
  const rows = [];
  (Array.isArray(records) ? records : []).forEach((p, i) => {
    if (!p || !p.name) return;
    const name = norm(p.name);
    const code = norm(p.cat);
    const category = norm(p.category);
    const desc = norm(p.desc);
    rows.push({
      rec: p,
      _id: i,
      name, code, category, desc,
      nameC: compact(name), codeC: compact(code),
      all: `${name} ${code} ${category} ${desc}`,
      allC: compact(`${name} ${code}`),
    });
  });
  return rows;
}

function tokenScore(t, r) {
  const tc = compact(t);
  if (!tc) return 0;
  // single letters (the "d" in "vit d") only count at the start of a word in the name
  if (tc.length < 2) return r.name.split(" ").some(w => w.startsWith(tc)) ? 30 : 0;
  if (r.name === t || r.nameC === tc) return 100;
  if (r.name.startsWith(t) || r.nameC.startsWith(tc)) return 60;
  if (r.name.split(" ").some(w => w.startsWith(t))) return 45;
  if (r.name.includes(t) || r.nameC.includes(tc)) return 35;
  if (r.codeC.includes(tc) || r.allC.includes(tc)) return 25;
  if (r.category.includes(t)) return 12;
  if (r.desc.includes(t)) return 5;
  return 0;
}

// Returns [{row, score}] sorted by relevance. Empty query returns null (caller shows browse view).
export function searchIndex(rows, query) {
  const q = norm(query);
  if (!q) return null;
  const tokens = q.split(" ").filter(Boolean).map(t => (ALIASES[t] ? [t, norm(ALIASES[t])] : [t]));
  const whole = compact(q);
  const out = [];
  for (const r of rows) {
    let total = 0;
    let ok = true;
    for (const variants of tokens) {
      // a word matches if it, or its common long form, is found
      const s = Math.max(...variants.map(v => tokenScore(v, r)));
      if (s === 0) { ok = false; break; }
      total += s;
    }
    // whole-phrase compact match rescues typed-together queries like "vitamind3"
    if (!ok && whole.length >= 3 && (r.nameC.includes(whole) || r.codeC.includes(whole))) { ok = true; total = 40; }
    if (ok) {
      if (r.nameC === whole) total += 200;
      out.push({ row: r, score: total });
    }
  }
  out.sort((a, b) => b.score - a.score || a.row.name.length - b.row.name.length);
  return out;
}

// Trigram similarity for "did you mean" when nothing matches (typos like "curcumen").
function grams(s) {
  const g = new Set();
  const x = `  ${s} `;
  for (let i = 0; i < x.length - 2; i++) g.add(x.slice(i, i + 3));
  return g;
}
export function suggest(rows, query, limit = 6) {
  const q = compact(norm(query));
  if (q.length < 3) return [];
  const qg = grams(q);
  const seen = new Set();
  return rows
    .map(r => {
      const first = compact(r.name.split(" ").slice(0, 2).join(" "));
      const g = grams(first);
      let inter = 0;
      qg.forEach(x => { if (g.has(x)) inter++; });
      return { row: r, sim: inter / (qg.size + g.size - inter) };
    })
    .filter(x => x.sim > 0.2)
    .sort((a, b) => b.sim - a.sim)
    .filter(x => { const k = x.row.rec.name; if (seen.has(k)) return false; seen.add(k); return true; })
    .slice(0, limit)
    .map(x => x.row.rec.name);
}
